<script setup lang="ts">
import { computed, inject, reactive, ref, watch, type Ref } from 'vue';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import type { ClinicalDrug } from '@/api/clinical-drugs';
import {
  medicationsApi, type DosageForm, type MedicationOrder, type MedicationOrderForm, type SuppliedBy,
} from '@/api/medications';
import type { SerializedUser } from '@/api/users';
import { useCompanyClock } from '@/composables/useCompanyClock';
import { useMobile } from '@/composables/useMobile';
import { allergiesTo, allergyLabel } from '@/utils/allergies';
import { apiErrorText } from '@/utils/api-errors';
import {
  dosageSummary, durationValid, humanMoment, periodEnd, periodStart, routeLabel, startValid, toLocalDateTime,
  type PeriodChoice,
} from '@/utils/medications';
import ResponsiveDialog from '@/components/responsive-dialog.vue';
import MedicationDosageStep from './medication-dosage-step.vue';
import MedicationDrugStep from './medication-drug-step.vue';
import MedicationOrderReview from './medication-order-review.vue';
import MedicationPeriodStep from './medication-period-step.vue';

// Transcribes a prescription in four steps (the drug and its route, its
// doses, when it starts and how long it lasts, and a read-back to review and
// save), or changes an order (`replacing`, three steps: the drug is kept).
// Orders are never edited: a change creates a new order from a moment on
// ("desde mañana") and the current one counts until then.
const props = defineProps<{ patientId: number; replacing?: MedicationOrder | null }>();
const emit = defineEmits<{ close: []; changeOrder: [order: MedicationOrder] }>();

type StepKey = 'drug' | 'dosage' | 'period' | 'review';

const queryClient = useQueryClient();
const { isPhone } = useMobile();
const { now, companyTime } = useCompanyClock();
const currentUser = inject<Ref<SerializedUser> | undefined>('currentUser', undefined);
const inventoryEnabled = computed(() => !!currentUser?.value?.companyInventoryEnabled);
const replacing = props.replacing ?? null;
const isChange = !!replacing;
const error = ref('');

const drug = ref<ClinicalDrug | null>(null);
const highAlert = computed(() => replacing?.highAlert ?? drug.value?.highAlert ?? false);

// A high-alert dose is typed, never taken from a default.
function emptyDosage(): DosageForm {
  return {
    doseQuantity: highAlert.value ? null : 1, kind: 'scheduled', timesOfDay: [], everyDay: true, weekdays: [],
    maxDosesPer24h: null, indication: '', instructions: '',
  };
}

const EVENING = 1200;

// Another dose on the same order usually comes at night.
function extraDosage(): DosageForm {
  return { ...emptyDosage(), doseQuantity: null, timesOfDay: [EVENING] };
}

function dosagesFrom(order: MedicationOrder): DosageForm[] {
  return order.dosages.map((dosage) => {
    let kind: DosageForm['kind'] = 'scheduled';
    if (dosage.asNeeded) kind = 'as_needed';
    else if (dosage.singleDose) kind = 'single';

    return {
      doseQuantity: dosage.doseQuantity, kind, timesOfDay: [...dosage.timesOfDay],
      everyDay: dosage.weekdays.length === 0, weekdays: [...dosage.weekdays],
      maxDosesPer24h: dosage.maxDosesPer24h, indication: dosage.indication ?? '',
      instructions: dosage.instructions ?? '',
    };
  });
}

// A change keeps the end of the order it replaces: changing the dose halfway
// through a 7-day course still ends with the course.
function periodFrom(order: MedicationOrder | null): PeriodChoice {
  const endsAt = order?.endsAt ? toLocalDateTime(companyTime(new Date(order.endsAt))) : '';

  return {
    startMode: 'now', startsAt: toLocalDateTime(now()), durationMode: endsAt ? 'until' : 'open', durationDays: 7,
    endsAt,
  };
}

const route = ref(replacing?.route ?? '');
const dosages = ref<DosageForm[]>(replacing ? dosagesFrom(replacing) : [emptyDosage()]);
const suppliedBy = ref<SuppliedBy>(replacing?.suppliedBy ?? 'company');
const period = reactive<PeriodChoice>(periodFrom(replacing));
const prescription = reactive({
  prescriberName: replacing?.prescriberName ?? '',
  prescriberIdentifier: replacing?.prescriberIdentifier ?? '',
  prescribedOn: replacing?.prescribedOn ?? '',
  prescriptionDocument: null as File | null,
});
const reason = ref('');

const drugName = computed(() => replacing?.drugName ?? drug.value?.displayName ?? '');
const doseUnit = computed(() => replacing?.doseUnit ?? drug.value?.doseUnit ?? '');
const doseUnitLabel = computed(() => replacing?.doseUnitLabel ?? drug.value?.doseUnitLabel ?? '');

// Supplied by the company, a dose discounts stock only when the drug is in
// the company's inventory.
const stockEffect = computed(() => {
  if (!inventoryEnabled.value || !drug.value) return '';

  return drug.value.inventoryItemId ? 'descuenta stock' : 'no está en inventario, no descuenta stock';
});

// Orders in force or upcoming, to warn about the same drug, or the same
// active ingredient, twice.
const { data: inForce } = useQuery({
  queryKey: ['medicationOrders', props.patientId, 'in_force'],
  queryFn: () => medicationsApi.orders(props.patientId, 'in_force'),
  enabled: !isChange,
});
const { data: upcoming } = useQuery({
  queryKey: ['medicationOrders', props.patientId, 'upcoming'],
  queryFn: () => medicationsApi.orders(props.patientId, 'upcoming'),
  enabled: !isChange,
});
// The patient's allergies to the drug's active ingredients: a warning that the
// user must acknowledge before saving, never a block (the prescription may say so).
const { data: allergies } = useQuery({
  queryKey: ['patientAllergies', props.patientId],
  queryFn: () => medicationsApi.allergies(props.patientId),
});
const allergyMatches = computed(() => (
  allergiesTo(allergies.value ?? [], replacing?.substanceIds ?? drug.value?.substanceIds ?? [])
));
// "penicilina (Grave) · urticaria · mariscos".
const allergyWarning = computed(() => allergyMatches.value
  .map((allergy) => [allergyLabel(allergy), allergy.reaction].filter(Boolean).join(' · '))
  .join(' · '));
const allergyChecked = ref(false);
watch(drug, () => {
  allergyChecked.value = false;
});
const allergyCheckMissing = computed(() => allergyMatches.value.length > 0 && !allergyChecked.value);

const duplicates = computed(() => {
  const chosen = drug.value;
  if (!chosen) return [];

  return [...(inForce.value ?? []), ...(upcoming.value ?? [])].filter((order) => (
    order.clinicalDrugId === chosen.id || order.substanceIds.some((id) => chosen.substanceIds.includes(id))
  ));
});

// Steps
const steps = computed(() => [
  ...(isChange ? [] : [{ key: 'drug' as StepKey, title: 'Medicamento' }]),
  { key: 'dosage' as StepKey, title: 'Dosis' },
  { key: 'period' as StepKey, title: 'Período' },
  { key: 'review' as StepKey, title: isChange ? 'Revisar cambio' : 'Revisar' },
]);
const current = ref(0);
const reached = ref(0);
const attempted = reactive<Record<StepKey, boolean>>({ drug: false, dosage: false, period: false, review: false });
const currentKey = computed(() => steps.value[current.value].key);
const lastStep = computed(() => current.value === steps.value.length - 1);

// An as-needed dose says when to give it; a scheduled one, at what times.
function scheduleValid(dosage: DosageForm): boolean {
  switch (dosage.kind) {
  case 'as_needed': return !!dosage.indication.trim();
  case 'single': return true;
  default: return dosage.timesOfDay.length > 0 && (dosage.everyDay || dosage.weekdays.length > 0);
  }
}

function dosageValid(dosage: DosageForm): boolean {
  return !!dosage.doseQuantity && dosage.doseQuantity > 0 && scheduleValid(dosage);
}

const validity = computed<Record<StepKey, boolean>>(() => ({
  drug: isChange || (!!drug.value && !!route.value),
  dosage: dosages.value.every(dosageValid),
  period: startValid(period) && durationValid(period, now()),
  review: true,
}));

const startError = computed(() => (attempted.period && !startValid(period) ? 'Indica la fecha y hora' : ''));

const durationError = computed(() => {
  if (!attempted.period || durationValid(period, now())) return '';
  if (period.durationMode === 'days') return 'Al menos 1 día';

  return periodEnd(period, now()) ? 'Debe ser después del inicio' : 'Indica la fecha y hora';
});

// When it starts and how long it lasts, under the period step's title.
function periodSummary(): string {
  const start = periodStart(period);
  const end = periodEnd(period, now());
  const from = start ? humanMoment(start, now()) : 'Ahora';
  const days = period.durationDays === 1 ? '1 día' : `${period.durationDays} días`;
  if (period.durationMode === 'days') return `${from} · ${days}`;

  return end ? `${from} · hasta ${humanMoment(end, now())}` : `${from} · sin término`;
}

// What each finished step chose, under its title (not on the read-back,
// which already says it all).
const subtitles = computed<Record<StepKey, string>>(() => ({
  drug: drug.value ? `${drug.value.displayName} · ${routeLabel(route.value)}` : '',
  dosage: validity.value.dosage ?
    dosages.value.map((dosage) => dosageSummary(dosage, doseUnitLabel.value)).join(' · ') :
    '',
  period: validity.value.period ? periodSummary() : '',
  review: '',
}));

function goTo(index: number) {
  if (index <= reached.value) current.value = index;
}

function goToStep(key: StepKey) {
  goTo(steps.value.findIndex((step) => step.key === key));
}

function next() {
  if (!validity.value[currentKey.value]) {
    attempted[currentKey.value] = true;

    return;
  }
  current.value += 1;
  reached.value = Math.max(reached.value, current.value);
}

// Picking a drug sets its usual route and, before its dose is set, a fresh
// dose in its unit. It stays on the step: the route and the orders it
// duplicates are read there before going on.
watch(drug, (chosen) => {
  if (!chosen) return;
  route.value = chosen.defaultRoute;
  if (reached.value < 1) dosages.value = [emptyDosage()];
});

function orderForm(): MedicationOrderForm {
  const start = periodStart(period);
  const end = periodEnd(period, now());

  return {
    clinicalDrugId: drug.value?.id,
    route: route.value,
    suppliedBy: suppliedBy.value,
    // Empty means "now", decided by the server in the company time zone.
    startsAt: start ? toLocalDateTime(start) : '',
    endsAt: end ? toLocalDateTime(end) : '',
    // A past start only dates the order: the doses before it was entered are
    // not listed as due, so none of them is marked omitted.
    retroactive: false,
    ...prescription,
    dosages: dosages.value,
  };
}

const save = useMutation({
  mutationFn: () => (
    replacing ?
      medicationsApi.replace(replacing.id, orderForm(), reason.value) :
      medicationsApi.create(props.patientId, orderForm())
  ),
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: ['medicationOrders', props.patientId] });
    emit('close');
  },
  onMutate: () => {
    error.value = '';
  },
  onError: (failure) => {
    error.value = apiErrorText(failure, 'No se pudo guardar la indicación.');
  },
});

function submit() {
  const invalid = steps.value.findIndex((step) => !validity.value[step.key]);
  if (invalid >= 0) {
    attempted[steps.value[invalid].key] = true;
    current.value = invalid;

    return;
  }
  if (allergyCheckMissing.value) {
    attempted.review = true;

    return;
  }
  save.mutate();
}
</script>
<template>
  <responsive-dialog
    :model-value="true"
    :title="isChange ? 'Cambiar indicación' : 'Nueva indicación'"
    max-width="720"
    scrollable
    persistent
    @close="emit('close')"
  >
    <v-stepper
      :model-value="current + 1"
      flat
      class="order-stepper"
    >
      <!-- Hidden, not removed, on phones: the window follows its items. -->
      <v-stepper-header v-show="!isPhone">
        <template
          v-for="(step, index) in steps"
          :key="step.key"
        >
          <v-divider v-if="index > 0" />
          <v-stepper-item
            :value="index + 1"
            :title="step.title"
            :subtitle="index < current && !lastStep ? subtitles[step.key] : ''"
            :complete="index < current"
            :editable="index <= reached && index !== current"
            :color="index <= current ? 'primary' : undefined"
            @click="goTo(index)"
          />
        </template>
      </v-stepper-header>
      <div
        v-if="isPhone"
        class="text-subtitle-2 text-medium-emphasis"
      >
        Paso {{ current + 1 }} de {{ steps.length }} · {{ steps[current].title }}
      </div>

      <v-stepper-window>
        <v-stepper-window-item
          v-for="(step, index) in steps"
          :key="step.key"
          :value="index + 1"
        >
          <div class="pt-4">
            <medication-drug-step
              v-if="step.key === 'drug'"
              v-model:drug="drug"
              v-model:route="route"
              :duplicates="duplicates"
              :show-errors="attempted.drug"
              @change-order="emit('changeOrder', $event)"
            />

            <medication-dosage-step
              v-else-if="step.key === 'dosage'"
              v-model:dosages="dosages"
              :drug-name="drugName"
              :route="route"
              :dose-unit="doseUnit"
              :dose-unit-label="doseUnitLabel"
              :high-alert="highAlert"
              :is-change="isChange"
              :start="periodStart(period)"
              :show-errors="attempted.dosage"
              @add-dosage="dosages.push(extraDosage())"
            />

            <medication-period-step
              v-else-if="step.key === 'period'"
              v-model:period="period"
              :dosages="dosages"
              :is-change="isChange"
              :start-error="startError"
              :duration-error="durationError"
            />

            <medication-order-review
              v-else
              v-model:period="period"
              v-model:supplied-by="suppliedBy"
              v-model:prescription="prescription"
              v-model:reason="reason"
              :drug-name="drugName"
              :route="route"
              :high-alert="highAlert"
              :dose-unit-label="doseUnitLabel"
              :dosages="dosages"
              :replacing="replacing"
              :stock-effect="stockEffect"
              @edit-drug="goToStep('drug')"
              @edit-dosage="goToStep('dosage')"
              @edit-period="goToStep('period')"
            />
          </div>
        </v-stepper-window-item>
      </v-stepper-window>
    </v-stepper>
    <v-alert
      v-if="allergyMatches.length > 0 && (currentKey === 'drug' || currentKey === 'review')"
      type="error"
      variant="tonal"
      density="compact"
      title="El paciente es alérgico"
      class="mt-4"
    >
      <div class="text-body-2">
        {{ allergyWarning }}
      </div>
      <v-checkbox
        v-if="currentKey === 'review'"
        v-model="allergyChecked"
        label="Revisé la alergia: la receta indica este medicamento"
        density="compact"
        :error-messages="attempted.review && allergyCheckMissing ? 'Confírmalo para guardar' : ''"
        :hide-details="!(attempted.review && allergyCheckMissing)"
      />
    </v-alert>
    <v-alert
      v-if="error"
      type="error"
      variant="tonal"
      :text="error"
      class="mt-4"
    />

    <template #actions>
      <v-btn
        v-if="current > 0"
        prepend-icon="mdi-chevron-left"
        :disabled="save.isPending.value"
        @click="current -= 1"
      >
        Atrás
      </v-btn>
      <v-spacer />
      <v-btn
        v-if="!isPhone"
        :disabled="save.isPending.value"
        @click="emit('close')"
      >
        Cancelar
      </v-btn>
      <v-btn
        v-if="!lastStep"
        color="primary"
        variant="flat"
        append-icon="mdi-chevron-right"
        @click="next"
      >
        Siguiente
      </v-btn>
      <v-btn
        v-else
        color="primary"
        variant="flat"
        :loading="save.isPending.value"
        @click="submit"
      >
        {{ isChange ? 'Guardar cambio' : 'Guardar indicación' }}
      </v-btn>
    </template>
  </responsive-dialog>
</template>
<style scoped>
.order-stepper :deep(.v-stepper-header) {
  box-shadow: none;
  border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.order-stepper :deep(.v-stepper-window) {
  margin: 0;
}

.order-stepper :deep(.v-stepper-item__subtitle) {
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
